import { AppWindow, Maximize2, Minimize2, X } from 'lucide-react';
import { Button, Icon } from '@ideoz/ui';
import { ApiUrls } from '@ideoz/core';
import { StreamedArtifactSidePanel } from '../../artifacts';
import { PrototyperPanel } from '../../prototyper';
import { PrototyperPublishMenu } from '../../prototyper/components/PrototyperPublishMenu';
import { useConversation } from '../context/ConversationContext';
import type { useStreamedArtifactPanel } from '../hooks/useStreamedArtifactPanel';
import type { ConversationFindings } from '../hooks/useConversationFindings';

/**
 * The secondary right-hand panel of the conversation workspace (extracted from
 * `ConversationModule` in Phase 2, slice 3): the prototype panel (with its
 * snapshot-restore error banner and the embedded WebContainer workbench) and
 * the streamed-artifact side panel, which share the same resizable slot.
 *
 * Presentational. The prototype/artifact **layout** (open/collapse, the panel
 * ref, `isSecondaryOpen`) lives in `usePrototypePanel`, and the `<ResizablePanel>`
 * wrapper + `prototypePanelRef` stay in the module (the panel must remain a
 * direct child of the resizable group). The build-stream orchestration stays in
 * the module too. This component only renders the two conditional children —
 * everything on `facade.prototyper`/`facade.header` and the workbench-stream
 * values are read from `useConversation()`; module-owned handlers/flags arrive
 * as props.
 */
export interface PrototyperPaneProps {
  isPrototypeOpen: boolean;
  isConversationPanelCollapsed: boolean;
  onClose: () => void;
  onPublish: (slug: string | null, options?: { republish?: boolean }) => void | Promise<void>;
  onToggleConversationPanel: () => void;
  currentPrototypeId: number | null;
  currentPrototypeSlug: string | null;
  currentProjectId: string | null;
  /** The single findings-state instance owned by ConversationModule (design-insights open). */
  findings: ConversationFindings;
  /** The streamed-artifact side-panel state (shares this slot). */
  streamedArtifact: ReturnType<typeof useStreamedArtifactPanel>;
  conversationDirection: 'rtl' | 'ltr' | undefined;
}

export function PrototyperPane({
  isPrototypeOpen,
  isConversationPanelCollapsed,
  onClose,
  onPublish,
  onToggleConversationPanel,
  currentPrototypeId,
  currentPrototypeSlug,
  currentProjectId,
  findings,
  streamedArtifact,
  conversationDirection,
}: PrototyperPaneProps): JSX.Element {
  const { facade, workbench } = useConversation();

  // When the WebContainer workbench is live, the prototyper's header controls are
  // merged INTO the Workbench toolbar (one bar, not two): the title on its left,
  // and fullscreen · Publish · close on its right. `PrototyperPanel` then hides its
  // own header (`hideHeader`). Non-streaming paths keep the panel's own header.
  const isWorkbenchLive = Boolean(workbench.isStreamingPrototype);
  const workbenchTitleSlot = (
    <div className="flex min-w-0 items-center gap-2">
      <AppWindow size={16} className="text-primary-500 shrink-0" />
      <h2 className="truncate text-sm font-semibold text-gray-900 dark:text-gray-100">
        Prototype View
      </h2>
    </div>
  );
  // Collapse/expand is the FIRST button on the right side (its own lead slot,
  // before the view toggle). It's bordered like the other controls; only the
  // close button is borderless.
  const workbenchLeadSlot = (
    <Button
      onClick={onToggleConversationPanel}
      variant="outline"
      size="sm"
      className="!p-1.5 h-8 w-8 shrink-0"
      aria-label={
        isConversationPanelCollapsed ? 'Show conversation panel' : 'Hide conversation panel'
      }
    >
      <Icon icon={isConversationPanelCollapsed ? Minimize2 : Maximize2} size={16} />
    </Button>
  );
  const workbenchActionsSlot = (
    <>
      <PrototyperPublishMenu
        onPublish={onPublish}
        prototyperDemoLinkId={currentPrototypeId || undefined}
        prototyperSlug={currentPrototypeSlug || undefined}
        userName={facade.header.headerProps.user?.name}
        platformLink={ApiUrls.platformLink}
        publishReady={workbench.streamingPublishReady}
        disabledReason={workbench.streamingPublishDisabledReason}
        previewReady={workbench.streamingPreviewReady}
        isLoading={workbench.streamingWorkbenchLoading}
      />
      <Button
        onClick={onClose}
        variant="ghost"
        size="sm"
        className="!p-1.5 h-8 w-8"
        aria-label="Close prototype view"
      >
        <Icon icon={X} size={16} />
      </Button>
    </>
  );

  return (
    <>
      {isPrototypeOpen && (
        <PrototyperPanel
          isOpen={isPrototypeOpen}
          hideHeader={isWorkbenchLive}
          onClose={onClose}
          // Only the true cold-start wait (WebContainer booting, snapshot bootstrap pending)
          // owns this illustrated animation — `isWorkbenchBuildLive` used to also gate it,
          // which unmounted/remounted `WorkbenchPanel`/`Workbench` mid-session (losing its
          // local iframe/bridge state) every time it flickered, causing the loading text to
          // jump around. Once cold-start clears, `Workbench`'s own loading-phase ladder
          // (`deriveWorkbenchLoadingPhase`) is the sole, continuous owner of loading text.
          isLoading={workbench.isStreamingPrototype ? workbench.streamingWorkbenchLoading : false}
          error={facade.prototyper.prototypeError}
          onPublish={onPublish}
          prototyperDemoLinkId={currentPrototypeId || undefined}
          prototyperSlug={currentPrototypeSlug || undefined}
          userName={facade.header.headerProps.user?.name}
          platformLink={ApiUrls.platformLink}
          onDesignInsightsClick={currentProjectId ? findings.openDesignInsights : undefined}
          onToggleConversationPanel={onToggleConversationPanel}
          isConversationPanelCollapsed={isConversationPanelCollapsed}
          publishReady={
            workbench.isStreamingPrototype ? workbench.streamingPublishReady : undefined
          }
          publishDisabledReason={
            workbench.isStreamingPrototype ? workbench.streamingPublishDisabledReason : null
          }
          // Publish can't deploy anything on the legacy (non-WebContainer) path — hide it
          // there entirely instead of offering a control that's guaranteed to fail.
          publishUnavailable={!workbench.isStreamingPrototype}
          previewReady={workbench.isStreamingPrototype ? workbench.streamingPreviewReady : true}
        >
          {workbench.isStreamingPrototype ? (
            <>
              {workbench.workbenchSnapshotBootstrap === 'failed' &&
              workbench.workbenchSnapshotRestoreError ? (
                <div className="mx-3 mb-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800 flex flex-wrap items-center gap-2">
                  <span>
                    Could not restore your last prototype snapshot:{' '}
                    {workbench.workbenchSnapshotRestoreError}. Auto-build is paused until you retry
                    or start a new build.
                  </span>
                  {workbench.onRetrySnapshotBootstrap ? (
                    <button
                      type="button"
                      className="underline font-medium"
                      onClick={workbench.onRetrySnapshotBootstrap}
                    >
                      Retry restore
                    </button>
                  ) : null}
                </div>
              ) : null}
              {workbench.workbenchPersistError ? (
                <div className="mx-3 mb-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800 flex flex-wrap items-center gap-2">
                  <span>{workbench.workbenchPersistError}</span>
                  {workbench.onCancelWorkbenchStream ? (
                    <button
                      type="button"
                      className="underline font-medium"
                      onClick={workbench.onCancelWorkbenchStream}
                    >
                      Cancel
                    </button>
                  ) : null}
                </div>
              ) : null}
              {workbench.renderWorkbenchPanel?.({
                isOpen: isPrototypeOpen,
                onClose,
                onToggleConversationPanel,
                isConversationPanelCollapsed,
                embedded: true,
                headerLeftSlot: workbenchTitleSlot,
                headerRightLeadSlot: workbenchLeadSlot,
                headerRightSlot: workbenchActionsSlot,
              })}
            </>
          ) : (
            <div className="flex flex-1 items-center justify-center p-6 text-center text-sm text-gray-500">
              Live prototyping needs a browser that supports cross-origin isolation. Try the
              latest Chrome, Edge, or Firefox to build and publish a prototype here.
            </div>
          )}
        </PrototyperPanel>
      )}
      {streamedArtifact.isOpen && !isPrototypeOpen && (
        <StreamedArtifactSidePanel
          artifact={streamedArtifact.activeArtifact}
          isOpen={streamedArtifact.isOpen}
          onClose={streamedArtifact.close}
          onToggleConversationPanel={onToggleConversationPanel}
          isConversationPanelCollapsed={isConversationPanelCollapsed}
          isRtl={conversationDirection === 'rtl'}
          onSave={streamedArtifact.save}
          onSaveStructured={streamedArtifact.saveStructured}
          onShare={streamedArtifact.share}
        />
      )}
    </>
  );
}

export default PrototyperPane;
